import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ProjectStatus, UploadState } from '../../../shared/types';
import Chip, { type ChipTone } from '../Chip';
import Eyebrow from '../Eyebrow';
import Toaster from '../Toaster';
import Wordmark from '../Wordmark';
import { authApi } from '../../lib/api';
import AdminThemeToggle from './AdminThemeToggle';

export const primaryButtonClass =
  'app-button inline-flex min-h-[46px] min-w-[44px] items-center justify-center gap-2 rounded-full border border-accent bg-accent px-5 py-2 text-sm font-semibold text-accent-foreground no-underline transition-all duration-300 hover:border-accent-hover hover:bg-accent-hover disabled:cursor-wait disabled:opacity-55 disabled:hover:translate-y-0';

export const primaryButtonGlowClass = `${primaryButtonClass} app-button-glow`;

export const secondaryButtonClass =
  'app-button inline-flex min-h-[46px] min-w-[44px] items-center justify-center gap-2 rounded-full border border-line bg-transparent px-5 py-2 text-sm font-semibold text-fg no-underline transition-all duration-300 hover:border-fg/60 hover:bg-elevated disabled:cursor-wait disabled:opacity-55 disabled:hover:translate-y-0';

export const quietButtonClass =
  'app-button inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-full border border-transparent bg-transparent px-4 py-2 text-sm font-semibold text-muted no-underline transition-all duration-300 hover:text-fg disabled:cursor-wait disabled:opacity-55 disabled:hover:translate-y-0';

// leading-[1.3] matches the mockup's .btn-sm: an arbitrary text-[13px] carries no
// paired line-height, so without it the pill inherits body 1.65 and stands ~35px
// tall instead of the ~30px the button scale is drawn at.
export const secondarySmButtonClass =
  'app-button inline-flex items-center justify-center gap-2 rounded-full border border-line bg-transparent px-3 py-1.5 text-[13px] font-semibold leading-[1.3] text-fg no-underline transition-all duration-300 hover:border-fg/60 hover:bg-elevated disabled:cursor-wait disabled:opacity-55 disabled:hover:translate-y-0';

export const quietSmButtonClass =
  'app-button inline-flex items-center justify-center gap-2 rounded-full border border-transparent bg-transparent px-3 py-1.5 text-[13px] font-semibold leading-[1.3] text-muted no-underline transition-all duration-300 hover:text-fg disabled:cursor-wait disabled:opacity-55 disabled:hover:translate-y-0';

export const inputClass =
  'mt-2 min-h-[46px] w-full rounded-input border border-line bg-card px-3 py-2 text-fg placeholder:text-faint focus:border-accent disabled:opacity-60';

export const cardClass = 'rounded-card border border-line bg-card shadow-card';

export function AdminShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  async function signOut() {
    await authApi.logout();
    navigate('/admin/login', { replace: true });
  }

  return (
    <main className="min-h-screen bg-page text-fg">
      <header className="sticky top-0 z-30 border-b border-line bg-page/95 backdrop-blur">
        <div className="mx-auto flex min-h-16 max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-2 md:h-16 md:min-h-0 md:flex-nowrap md:px-10 md:py-0">
          <Link to="/admin" className="flex items-center gap-3 text-fg no-underline">
            <Wordmark size="small" />
            <span className="ml-1 border-l border-line pl-3 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
              Review admin
            </span>
          </Link>
          <nav className="flex flex-wrap items-center gap-2 md:flex-nowrap" aria-label="Admin navigation">
            <AdminThemeToggle />
            <Link to="/admin/storage" className={quietSmButtonClass}>Storage</Link>
            <Link to="/admin/security" className={quietSmButtonClass}>Security</Link>
            <button type="button" className={quietSmButtonClass} onClick={signOut}>Sign out</button>
          </nav>
        </div>
      </header>
      {children}
      <Toaster />
    </main>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  level = 'section',
}: {
  eyebrow?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  level?: 'page' | 'section';
}) {
  const Heading = level === 'page' ? 'h1' : 'h2';
  const eyebrowNode = typeof eyebrow === 'string'
    ? <Eyebrow accent={level === 'page'}>{eyebrow}</Eyebrow>
    : eyebrow;

  return (
    <div>
      {eyebrowNode}
      {title ? (
        <Heading className={`${level === 'page' ? 'title-xl' : 'title-section'}${eyebrow ? ' mt-3' : ''}`}>
          {title}
        </Heading>
      ) : null}
      {description ? (
        <p className={`${title || eyebrow ? 'mt-2 ' : ''}max-w-2xl text-muted ${level === 'page' ? 'text-[15px]' : 'text-sm'} leading-[1.55]`}>
          {description}
        </p>
      ) : null}
    </div>
  );
}

const projectStatusTones: Record<ProjectStatus, ChipTone> = {
  draft: 'warning',
  active: 'teal',
  completed: 'success',
  expired: 'error',
  deleted: 'error',
};

const uploadStatusTones: Record<UploadState, ChipTone> = {
  pending: 'warning',
  uploading: 'warning',
  uploaded: 'success',
  failed: 'error',
};

export function StatusChip({ status }: { status: ProjectStatus }) {
  return <Chip tone={projectStatusTones[status]} dot>{status}</Chip>;
}

export function UploadChip({ state }: { state: UploadState }) {
  return <Chip tone={uploadStatusTones[state]} dot>{state === 'pending' ? 'No media' : state}</Chip>;
}

export function StatePanel({
  eyebrow,
  title,
  children,
  tone = 'plain',
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
  tone?: 'plain' | 'error' | 'tint';
}) {
  const toneClass =
    tone === 'error'
      ? 'border-error/30 bg-error-tint'
      : tone === 'tint'
        ? 'border-accent/25 bg-accent-tint'
        : 'border-line bg-card';
  return (
    <section className={`rounded-card border p-6 shadow-card ${toneClass}`}>
      <Eyebrow accent={tone === 'tint'} tone={tone === 'error' ? 'error' : 'default'}>{eyebrow}</Eyebrow>
      <h2 className="title-lg mt-2">{title}</h2>
      <div className="mt-3 max-w-2xl text-muted">{children}</div>
    </section>
  );
}

export function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <span className="block font-mono text-[11px] uppercase tracking-[0.14em] text-faint">
      {children}
    </span>
  );
}

export function AdminFooter({ path }: { path: string }) {
  return (
    <footer className="site-footer mt-20">
      <div className="footer-row mx-auto max-w-6xl px-5 md:px-10">
        <span>Sheahan Studios · Review admin</span>
        <span>{path}</span>
      </div>
    </footer>
  );
}

export function formatDate(value: number | null): string {
  if (value === null) return 'No expiry';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Melbourne',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(value);
}

export function formatDateTime(value: number): string {
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Melbourne',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(value);
}

export function formatShortDate(value: number): string {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: 'Australia/Melbourne',
    day: 'numeric',
    month: 'short',
  }).formatToParts(value);
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value.slice(0, 3) ?? '';
  return `${day} ${month}`.trim();
}

export function formatBytes(value: number): string {
  if (value < 1_000) return `${value} B`;
  if (value < 1_000_000) return `${(value / 1_000).toFixed(1)} KB`;
  if (value < 1_000_000_000) return `${(value / 1_000_000).toFixed(1)} MB`;
  return `${(value / 1_000_000_000).toFixed(2)} GB`;
}

export function expirySummary(value: number | null, now: number = Date.now()): string {
  if (value === null) return 'Indefinite';
  const days = Math.ceil((value - now) / 86_400_000);
  if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
  if (days === 0) return 'Expires today';
  return `${days} day${days === 1 ? '' : 's'} remaining`;
}

export function formatDuration(seconds: number | null): string {
  if (seconds === null) return 'Not measured';
  const whole = Math.max(0, Math.round(seconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const remainder = whole % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
    : `${minutes}:${String(remainder).padStart(2, '0')}`;
}
